// React Query hooks for /api/v1/installations.
//
// A Installation is the platform's runtime primitive — it answers, for one
// customer, which connections + config + capability version make up a running
// integration. Polymorphic target (workflow | connector_action | integration |
// agent); the embed and dashboard mostly create target_type='workflow' rows.
//
// Permission model on the backend:
//   - GET:    widgets.read       (viewer can list/inspect)
//   - POST/PUT/DELETE: workflows.update (admin/developer/end_user can write)

import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { apiFetch, post, put, del } from "@/lib/api";
import { queryClient } from "@/lib/query-client";
import { keys } from "@/hooks/keys";
import { API_BASE } from "@/lib/utils";

// ── Types ────────────────────────────────────────────────────────────────────

export type InstallationTargetType = "widget" | "workflow" | "trigger" | "connector_action" | "integration" | "agent";
export type InstallationStatus     = "draft" | "ready" | "active" | "paused" | "error" | "archived";
export type InstallationProvenance = "human" | "agent";

export interface Installation {
  id: string;
  urn: string;
  orgId: string;
  endOrgId: string;
  /** Customer's slug + name joined from orgs — for UI display. */
  endOrgSlug: string | null;
  endOrgName: string | null;
  widgetId: string | null;
  sourceTemplateId: string | null;
  targetType: InstallationTargetType;
  targetId: string;
  targetVersion: string | null;
  label: string;
  slug: string;
  userId: string | null;
  /** connector slug → connection id pin map. */
  connections: Record<string, string>;
  configId: string | null;
  /** Inline config overrides, deep-merged over the configId template at run time. */
  config: Record<string, unknown>;
  status: InstallationStatus;
  healthStatus: string;
  provenance: InstallationProvenance;
  rationale: Record<string, unknown> | null;
  /** Widget trigger-category picks made at install time; null = no selection
   *  (every template auto-provisions). */
  selectedTriggerCategories?: string[] | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateInstallationInput {
  targetType: InstallationTargetType;
  targetId: string;
  targetVersion?: string | null;
  endOrgId: string;
  widgetId?: string | null;
  sourceTemplateId?: string | null;
  label: string;
  slug?: string;
  userId?: string | null;
  connections?: Record<string, string>;
  configId?: string | null;
  config?: Record<string, unknown>;
  status?: InstallationStatus;
  provenance?: InstallationProvenance;
  rationale?: Record<string, unknown> | null;
  /** Skip the server-side template auto-clone so the row is created with no
   *  config_id. The caller pins one via POST /configs { installation_id }. */
  deferConfig?: boolean;
  /** Widget trigger categories to auto-provision. Omit = all (legacy). */
  selectedTriggerCategories?: string[] | null;
}

export interface UpdateInstallationInput {
  targetVersion?: string | null;
  widgetId?: string | null;
  label?: string;
  slug?: string;
  connections?: Record<string, string>;
  configId?: string | null;
  config?: Record<string, unknown>;
  status?: InstallationStatus;
  healthStatus?: string;
  rationale?: Record<string, unknown> | null;
  /** Selection edit reconciles trigger instances both ways: newly covered
   *  templates are provisioned, removed categories' instances are deleted. */
  selectedTriggerCategories?: string[] | null;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

export function buildQuery(params: {
  targetType?: InstallationTargetType;
  targetId?: string;
  endOrgId?: string;
  widgetId?: string;
}) {
  const qs = new URLSearchParams();
  if (params.targetType) qs.set("target_type", params.targetType);
  if (params.targetId)   qs.set("target_id", params.targetId);
  if (params.endOrgId)   qs.set("end_org_id", params.endOrgId);
  if (params.widgetId)   qs.set("widget_id", params.widgetId);
  const s = qs.toString();
  return s ? `?${s}` : "";
}

export function toBody(input: CreateInstallationInput | UpdateInstallationInput): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if ("targetType"       in input && input.targetType       !== undefined) body.target_type        = input.targetType;
  if ("targetId"         in input && input.targetId         !== undefined) body.target_id          = input.targetId;
  if (input.targetVersion        !== undefined) body.target_version     = input.targetVersion;
  if ("endOrgId"         in input && input.endOrgId         !== undefined) body.end_org_id         = input.endOrgId;
  if (input.widgetId             !== undefined) body.widget_id          = input.widgetId;
  if ("sourceTemplateId" in input && input.sourceTemplateId !== undefined) body.source_template_id = input.sourceTemplateId;
  if (input.label                !== undefined) body.label              = input.label;
  if (input.slug                 !== undefined) body.slug               = input.slug;
  if ("userId"           in input && input.userId           !== undefined) body.user_id            = input.userId;
  if (input.connections          !== undefined) body.connections        = input.connections;
  if (input.configId             !== undefined) body.config_id          = input.configId;
  if (input.config               !== undefined) body.config             = input.config;
  if (input.status               !== undefined) body.status             = input.status;
  if ("provenance"       in input && input.provenance       !== undefined) body.provenance         = input.provenance;
  if (input.rationale            !== undefined) body.rationale          = input.rationale;
  if ("healthStatus"     in input && input.healthStatus     !== undefined) body.health_status      = input.healthStatus;
  if ("deferConfig"      in input && input.deferConfig      !== undefined) body.defer_config       = input.deferConfig;
  if (input.selectedTriggerCategories !== undefined) body.selected_trigger_categories = input.selectedTriggerCategories;
  return body;
}

// ── Queries ──────────────────────────────────────────────────────────────────

/** List bindings visible to the caller, optionally filtered by target + tenant + widget. */
export function useInstallations(
  params: { targetType?: InstallationTargetType; targetId?: string; endOrgId?: string; widgetId?: string } = {},
  opts?: { enabled?: boolean },
) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;
  const query = buildQuery(params);
  return useQuery({
    queryKey: params.targetId
      ? [...keys.installations.byTarget(orgId, params.targetType ?? "workflow", params.targetId), params.endOrgId ?? null, params.widgetId ?? null]
      : [...keys.installations.list(orgId), params.endOrgId ?? null, params.widgetId ?? null],
    queryFn: () => apiFetch<Installation[]>(`${API_BASE}/v1/installations${query}`),
    enabled: !!orgId && (opts?.enabled ?? true),
  });
}

/** Get a single binding by id. */
export function useInstallation(id?: string) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;
  return useQuery({
    queryKey: [...keys.installations.detail(orgId, id)],
    queryFn: () => apiFetch<Installation>(`${API_BASE}/v1/installations/${id}`),
    enabled: !!orgId && !!id,
  });
}

// ── Mutations ────────────────────────────────────────────────────────────────

/**
 * Everything a setup change moves server-side besides the setup itself, so the
 * views reading it refresh without a reload. A customer's workflow list fills
 * only once they have a setup (GET /workflows): the integration page kept its
 * workflows disabled after Connect until the page was reloaded. Creating or
 * removing a setup also makes or reaps its triggers and its config copy, and
 * the widget's insights and the setup journey's "customers connect" step count
 * setups.
 */
export function invalidateSetupDependents(orgId: string | undefined): void {
  for (const queryKey of [
    keys.installations.all(orgId),
    keys.workflows.all(orgId),
    keys.schedulers.all(orgId),
    keys.appTriggers.all(orgId),
    keys.webhooks.all(orgId),
    keys.configs.all(orgId),
    keys.customers.all(orgId),
    keys.onboarding.setup(orgId),
    ["widgets", orgId, "insights"],
    ["widgets", orgId, "embed-status"],
  ]) void queryClient.invalidateQueries({ queryKey });
}

export function useCreateInstallation() {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;
  return useMutation({
    mutationFn: (input: CreateInstallationInput) =>
      apiFetch<Installation>(`${API_BASE}/v1/installations`, post(toBody(input))),
    onSuccess: () => invalidateSetupDependents(orgId),
  });
}

export function useUpdateInstallation() {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateInstallationInput }) =>
      apiFetch<Installation>(`${API_BASE}/v1/installations/${id}`, put(toBody(patch))),
    onSuccess: () => invalidateSetupDependents(orgId),
  });
}

export function useDeleteInstallation() {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ deleted: boolean }>(`${API_BASE}/v1/installations/${id}`, del),
    onSuccess: () => {
      invalidateSetupDependents(orgId);
      // Backend cascade may reap connections (when the widget's
      // connector_refs[].deleteOnDeactivate=true and no other active
      // installation pins them) + the per-end-org config clone. Invalidate
      // both caches so the wizard's picker, the connections page, and
      // every other connection-driven view refresh.
      queryClient.invalidateQueries({ queryKey: keys.connections.all(orgId) });
    },
  });
}
